import { useState, useRef, useEffect, useMemo } from "react";

import {
  adminCustomerSheetsApi,
  useGetAdminCustomerSheetsQuery,
  useUpdateAdminCustomerSheetOrderMutation,
} from "../../../store/apiSlices/adminCustomerSheets";

import CustomerSheet from "./CustomerSheet";
import { DealTarckerTables, Table } from "../../../types/DealTracker";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../../store";
import { throttle } from "../../../utils";

export default function CustomerSheetList() {
  const { data, isLoading, isError, error, isFetching } =
    useGetAdminCustomerSheetsQuery(undefined, {
      refetchOnFocus: true,
      refetchOnReconnect: true,
    });
  const [updateTableOrder] = useUpdateAdminCustomerSheetOrderMutation();
  const [draggingId, setDraggingId] = useState<string | number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch<AppDispatch>();

  const canDragRef = useRef(true);

  const throttledDispatch = useMemo(
    () =>
      throttle((reordered: Table[]) => {
        dispatch(
          adminCustomerSheetsApi.util.updateQueryData(
            "getAdminCustomerSheets",
            undefined,
            (draft) => {
              draft.data = reordered;
            },
          ),
        );
      }, 50),
    [dispatch],
  );

  useEffect(() => {
    return () => throttledDispatch.cancel();
  }, [throttledDispatch]);

  if (isLoading && !data) return <TableSkeleton />;
  if (isError)
    return (
      <p className="text-center text-gray-500">
        {error?.data?.message || "Something went wrong!"}
      </p>
    );
  if (!data) return <p className="text-center text-gray-500">No data found!</p>;

  const reorderArray = (
    arr: DealTarckerTables["data"],
    fromId: string | number | null,
    toId: string | number,
  ) => {
    if (!fromId) return arr;
    const updated = [...arr];
    const fromIdx = updated.findIndex((t) => t.id === fromId);
    const toIdx = updated.findIndex((t) => t.id === toId);
    if (fromIdx === -1 || toIdx === -1) return arr;
    const [item] = updated.splice(fromIdx, 1);
    updated.splice(toIdx, 0, item);
    return updated.map((t, i) => ({ ...t, table_order: i + 1 }));
  };

  const handleMouseDownCapture = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;

    // If mouse down started inside grid → block drag
    if (target.closest("#ag-grid-root-container")) {
      canDragRef.current = false;
    } else {
      // Mouse down on outer card
      canDragRef.current = true;
    }
  };

  const handleDragStart =
    (e: React.DragEvent<HTMLDivElement>) => (id: string | number) => {
      if (!canDragRef.current) {
        e.preventDefault();
        return;
      }

      setDraggingId(id);
    };

  const handleDragOver = (
    e: React.DragEvent<HTMLDivElement>,
    targetId: string | number,
  ) => {
    e.preventDefault();
    if (!draggingId || draggingId === targetId) return;

    const fromIdx = data.data.findIndex((t) => t.id === draggingId);
    const toIdx = data.data.findIndex((t) => t.id === targetId);
    if (fromIdx === -1 || toIdx === -1 || fromIdx === toIdx) return;

    const reordered = reorderArray(data.data, draggingId, targetId);
    throttledDispatch(reordered);
  };

  const handleDrop = async () => {
    if (!draggingId) return;
    setDraggingId(null);

    // ✅ Only now call the mutation once with final payload
    const payload = data.data.map((t, i) => ({
      id: t.id,
      table_order: i + 1,
    }));

    await updateTableOrder(payload);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;

    const { clientY } = e;
    // if pointer goes above top or below bottom -> reset order
    if (clientY < rect.top || clientY > rect.bottom) {
      setDraggingId(null);
    }
  };

  // 🧩 Added: reset drag state if user drops outside
  const handleDragEnd = () => {
    setDraggingId(null);
  };

  const minOrder = Math.min(...data.data.map((t) => t.table_order ?? Infinity));
  const tables = structuredClone(data.data);

  if (!tables.length) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center border border-dashed border-gray-200 rounded-2xl bg-gray-50">
        {/* Icon */}
        <div className="mb-5">
          <svg
            className="w-16 h-16 text-gray-300"
            viewBox="0 0 24 24"
            fill="currentColor"
          >
            <path d="M4 3h10l6 6v12a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2zm9 1.5V10h5.5L13 4.5z" />
          </svg>
        </div>

        {/* Title */}
        <h3 className="text-sm font-semibold text-gray-800">No sheets yet</h3>

        {/* Description */}
        <p className="text-xs text-gray-500 mt-2 max-w-sm">
          You don’t have any sheets yet. Let’s create your first one!
        </p>

        {/* Optional CTA */}
        {/* <button className="mt-5 px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition">
          Create your first sheet
        </button> */}
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      onDragEnd={handleDragEnd} // <- global reset on drag end
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {tables.map((table) => {
        const isPinned = table.is_pin === 1;
        return (
          <div
            key={table.id}
            draggable={!isPinned}
            onMouseDownCapture={handleMouseDownCapture}
            onDragStart={
              !isPinned ? (e) => handleDragStart(e)(table.id) : undefined
            }
            onDragOver={
              !isPinned ? (e) => handleDragOver(e, table.id) : undefined
            }
            className={`mb-8 transition-all duration-200
              ${
                isPinned
                  ? "cursor-default"
                  : "cursor-grab active:cursor-grabbing"
              }
              ${
                !isPinned && draggingId === table.id
                  ? "opacity-50 scale-[0.98]"
                  : "opacity-100"
              }
            `}
          >
            <CustomerSheet
              table={table}
              minOrder={minOrder}
              isFetching={isFetching}
            />
          </div>
        );
      })}
    </div>
  );
}

function TableSkeleton() {
  return (
    <>
      {[1, 2, 3].map((el) => (
        <div
          key={el}
          className="animate-pulse h-[81px] bg-slate-300 rounded-xl mb-8"
        />
      ))}
    </>
  );
}
